import type { ResumeRequest } from '../types';

export interface ResumeMergeCounts {
  personalInfo: number;
  summary: boolean;
  experienceAdded: number;
  educationAdded: number;
  skillsAdded: number;
}

const expSignature = (e: ResumeRequest['experience'][number]): string => {
  const company = (e.company || '').trim().toLowerCase();
  const role = (e.role || '').trim().toLowerCase();
  if (!company || !role) return '';
  return `${company}|${role}|${(e.startDate || '').trim()}`;
};

const eduSignature = (e: ResumeRequest['education'][number]): string => {
  const institution = (e.institution || '').trim().toLowerCase();
  const degree = (e.degree || '').trim().toLowerCase();
  if (!institution || !degree) return '';
  return `${institution}|${degree}|${(e.startDate || '').trim()}`;
};

/**
 * Merge a parsed resume into the current working form without destroying
 * existing data:
 *  - personal info fields are filled only when currently blank (identity
 *    fields fullName/email/photo are never overwritten);
 *  - summary is filled only when empty;
 *  - experience/education/skills are appended when they don't already exist.
 */
export function mergeImportedResume(
  current: ResumeRequest,
  imported: ResumeRequest,
): { merged: ResumeRequest; counts: ResumeMergeCounts } {
  const merged: ResumeRequest = {
    templateId: current.templateId,
    personalInfo: { ...current.personalInfo },
    summary: current.summary,
    experience: [...current.experience],
    education: [...current.education],
    skills: [...current.skills],
  };

  let personalInfo = 0;
  (['phone', 'address', 'linkedin', 'website', 'photo'] as const).forEach((k) => {
    if (!(merged.personalInfo[k] || '').trim() && (imported.personalInfo[k] || '').trim()) {
      merged.personalInfo[k] = imported.personalInfo[k];
      personalInfo += 1;
    }
  });

  const summaryFilled = !(current.summary || '').trim() && !!(imported.summary || '').trim();
  if (summaryFilled) merged.summary = imported.summary;

  const expSeen = new Set(merged.experience.map(expSignature));
  let experienceAdded = 0;
  (imported.experience || []).forEach((e) => {
    const sig = expSignature(e);
    if (sig && !expSeen.has(sig)) {
      expSeen.add(sig);
      merged.experience.push(e);
      experienceAdded += 1;
    }
  });

  const eduSeen = new Set(merged.education.map(eduSignature));
  let educationAdded = 0;
  (imported.education || []).forEach((e) => {
    const sig = eduSignature(e);
    if (sig && !eduSeen.has(sig)) {
      eduSeen.add(sig);
      merged.education.push(e);
      educationAdded += 1;
    }
  });

  const skillsSeen = new Set(merged.skills.map((s) => s.toLowerCase()));
  let skillsAdded = 0;
  (imported.skills || []).forEach((s) => {
    const t = s.trim();
    if (t && !skillsSeen.has(t.toLowerCase())) {
      skillsSeen.add(t.toLowerCase());
      merged.skills.push(t);
      skillsAdded += 1;
    }
  });

  return { merged, counts: { personalInfo, summary: summaryFilled, experienceAdded, educationAdded, skillsAdded } };
}